import React, { useState, useEffect } from 'react';
import { X, CreditCard, Save, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { rateService } from '../services/rateService';
import { clubService } from '../services/clubService';

const RateForm = ({ isOpen, onClose, onSuccess, rateData = null, isEditing = false }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: rateData?.name || '',
    description: rateData?.description || '',
    price: rateData?.price || '',
    currency: rateData?.currency || '€',
    period: rateData?.period || 'mes',
    category: rateData?.category || 'Mensual',
    features: rateData?.features || [],
    active: rateData?.active ?? true,
    is_popular: rateData?.is_popular ?? false,
    club_id: rateData?.club_id || (user?.club_id || '')
  });

  const [featureInput, setFeatureInput] = useState('');
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [loadingClubs, setLoadingClubs] = useState(false);

  const isSuperAdmin = user?.role?.name === 'super_admin';
  const isClubUser = user?.role?.name === 'club';

  // Cargar clubs si es super admin
  useEffect(() => {
    if (isSuperAdmin && isOpen) {
      setLoadingClubs(true);
      clubService.getClubs()
        .then(response => {
          if (response.data) {
            setClubs(response.data);
          } else {
            setClubs(response);
          }
        })
        .catch(error => {
          console.error('Error al cargar clubs:', error);
        })
        .finally(() => {
          setLoadingClubs(false);
        });
    }
  }, [isSuperAdmin, isOpen]);

  // Actualizar el formulario cuando cambien los datos de la tarifa
  useEffect(() => {
    if (rateData) {
      setFormData({
        name: rateData.name || '',
        description: rateData.description || '',
        price: rateData.price || '',
        currency: rateData.currency || '€',
        period: rateData.period || 'mes',
        category: rateData.category || 'Mensual',
        features: rateData.features || [],
        active: rateData.active ?? true,
        is_popular: rateData.is_popular ?? false,
        club_id: rateData.club_id || (user?.club_id || '')
      });
    } else {
      setFormData({
        name: '',
        description: '',
        price: '',
        currency: '€',
        period: 'mes',
        category: 'Mensual',
        features: [],
        active: true,
        is_popular: false,
        club_id: user?.club_id || ''
      });
    }
    setFeatureInput('');
    setErrors({});
  }, [rateData, isOpen, user?.club_id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleAddFeature = () => {
    if (featureInput.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, featureInput.trim()]
      }));
      setFeatureInput('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre de la tarifa es obligatorio';
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'El precio debe ser mayor a 0';
    }

    if (!formData.club_id) {
      newErrors.club_id = 'Debes seleccionar un club';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        ...formData,
        price: parseFloat(formData.price),
        features: formData.features.length > 0 ? formData.features : null
      };

      const result = isEditing
        ? await rateService.updateRate(rateData.id, dataToSend)
        : await rateService.createRate(dataToSend);

      onSuccess?.(result);
      onClose();
    } catch (error) {
      console.error('Error al guardar la tarifa:', error);
      const errorMessage = error.response?.data?.message || 'Error al guardar la tarifa. Inténtalo de nuevo.';
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      currency: '€',
      period: 'mes',
      category: 'Mensual',
      features: [],
      active: true,
      is_popular: false,
      club_id: user?.club_id || ''
    });
    setFeatureInput('');
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <CreditCard size={24} className="text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? 'Editar Tarifa' : 'Crear Nueva Tarifa'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error general */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
              <AlertCircle size={20} className="text-red-600 mr-3" />
              <span className="text-red-800">{errors.submit}</span>
            </div>
          )}

          {/* Información básica */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Información Básica</h3>

            {/* Club - Solo para super admin */}
            {isSuperAdmin && (
              <div>
                <label htmlFor="club_id" className="block text-sm font-medium text-gray-700 mb-2">
                  Club *
                </label>
                {loadingClubs ? (
                  <div className="text-gray-500">Cargando clubs...</div>
                ) : (
                  <select
                    id="club_id"
                    name="club_id"
                    value={formData.club_id}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.club_id ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Selecciona un club</option>
                    {clubs.map(club => (
                      <option key={club.id} value={club.id}>
                        {club.name}
                      </option>
                    ))}
                  </select>
                )}
                {errors.club_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.club_id}</p>
                )}
              </div>
            )}

            {/* Nombre */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Nombre de la Tarifa *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ej: Plan Básico"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Descripción */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Descripción
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe la tarifa..."
              />
            </div>
          </div>

          {/* Precio y configuración */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Precio y Configuración</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Precio */}
              <div>
                <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                  Precio *
                </label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.price ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
                {errors.price && (
                  <p className="mt-1 text-sm text-red-600">{errors.price}</p>
                )}
              </div>

              {/* Moneda */}
              <div>
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
                  Moneda
                </label>
                <input
                  type="text"
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  maxLength="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="€"
                />
              </div>

              {/* Periodo */}
              <div>
                <label htmlFor="period" className="block text-sm font-medium text-gray-700 mb-2">
                  Periodo *
                </label>
                <select
                  id="period"
                  name="period"
                  value={formData.period}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="mes">Mes</option>
                  <option value="año">Año</option>
                  <option value="evento">Evento</option>
                </select>
              </div>
            </div>

            {/* Categoría */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                Categoría *
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Mensual">Mensual</option>
                <option value="Anual">Anual</option>
                <option value="Eventos">Eventos</option>
              </select>
            </div>
          </div>

          {/* Características */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Características</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddFeature();
                  }
                }}
                placeholder="Agregar característica..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={handleAddFeature}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                <Plus size={16} className="mr-1" />
                Agregar
              </button>
            </div>
            {formData.features.length > 0 && (
              <ul className="space-y-2">
                {formData.features.map((feature, index) => (
                  <li key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                    <span className="text-sm text-gray-700">{feature}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFeature(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Estado */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Estado</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="active"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="active" className="ml-2 block text-sm text-gray-700">
                  Tarifa activa
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_popular"
                  name="is_popular"
                  checked={formData.is_popular}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_popular" className="ml-2 block text-sm text-gray-700">
                  Marcar como popular
                </label>
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  {isEditing ? 'Actualizar Tarifa' : 'Crear Tarifa'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RateForm;
